import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import unionWith from 'lodash/unionWith';
import isEqual from 'lodash/isEqual';
import {
  API,
  GlobalActions,
  MessageRequestModel,
  MessageModel,
  ExpandedLocationsHelper,
  LoadingContent,
} from 'vibeguide';
import MessageDetails from './MessageDetails';
import MessageChat from './MessageChat';
import MessageHistory from './MessageHistory';
import './MessageSidebar.scss';

class MessageSidebar extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      message: new MessageRequestModel(),
      activeSection: 'details',
      copying: false,
      copiedMessage: false,
    };
  }

  componentDidMount() {
    const {
      messageId,
    } = this.props;

    if (messageId && messageId !== 'new') {
      this.getMessage();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      messageId,
      isNew,
      isCopy,
    } = this.props;

    const {
      copiedMessage,
    } = this.state;

    const {
      messageId: prevMessageId,
    } = prevProps;

    if ((messageId && !isNew && !isCopy && messageId !== prevMessageId)
      || (messageId && messageId !== prevMessageId)
      || (isCopy && !copiedMessage)
    ) {
      this.getMessage();
    }
  }

  /**
   * Update the message
   */
  onUpdate = (data = {}) => {
    const {
      message,
    } = this.state;

    /**
     * Merge the snapshot of resolutions on the message, with the resolutions from the selected locations
     */
    if (data.resolutions) {
      data.resolutions = unionWith(message.resolutions, data.resolutions, isEqual);
    }

    this.setState((state) => {
      return {
        message: {
          ...state.message,
          ...data,
        },
      };
    });
  };

  /**
   * Close the sidebar
   */
  onClose = () => {
    const {
      setPanel,
    } = this.props;

    setPanel({
      show: false,
    });
  };

  /**
   * Toggle the details section
   */
  onToggleDetails = () => {
    this.setState({
      activeSection: 'details',
    });
  };

  /**
   * Toggle the chat section
   */
  onToggleChat = () => {
    this.setState((state) => {
      return {
        activeSection: state.activeSection !== 'chat'
          ? 'chat'
          : 'details',
      };
    });
  };

  /**
   * Toggle the history section
   */
  onToggleHistory = () => {
    this.setState((state) => {
      return {
        activeSection: state.activeSection !== 'history'
          ? 'history'
          : 'details',
      };
    });
  };

  getMessage = async () => {
    const {
      type,
      messageId,
      isCopy,
      // isReRequest,
    } = this.props;

    const message = type === 'request'
      ? await API.MessageRequest.getById(messageId, {
        expand: 'messageblocks',
      })
      : await API.Message.getById(messageId);

    // message is about to be duplicated, remove data we don't want to copy over
    if (isCopy) {
      message._id = '';
      message.mediaSourceRequestId = message._id;
      message.name += ' Copy';
      message.clientApproval = {};
      message.partnerApproval = {};
      message.locations = [];
      message.status = '';
    }
    // } else if (isReRequest) {
    //   message._id = '';
    //   message.mediaSourceRequestId = message._id;
    //   message.status = '';
    // }

    const requestResponse = type === 'library'
      ? await API.MessageRequest.getById(message.requestId)
      : null;

    const request = requestResponse
      ? new MessageRequestModel(requestResponse)
      : {};

    /**
     * When message is not being duplicated
     * get the expanded locations and resolutions
     */
    if (!isCopy) {
      const expandedLocations = type === 'request'
        ? await API.MessageRequest.getExpandedLocations(messageId)
        : await API.Message.getExpandedLocations(messageId);

      // Set the location data
      message.locationsData = ExpandedLocationsHelper.getLocationsData(expandedLocations);

      // get the location resolutions
      if (message.mediaFormat === 'visual') {
        const locationResolutions = message.locations.length > 0 ? await API.Location.resolutions({
          locations: message.locations,
        }) : [];

        // merge the resolutions from the message snapshot with resolutions from the locations selected
        message.resolutions = unionWith(message.resolutions, locationResolutions, isEqual);
      }
    }

    const messageModel = type === 'request'
      ? new MessageRequestModel(message)
      : new MessageModel({
        ...message,
        script: request.script,
      });

    const combinedData = {
      ...request,
      ...messageModel,
    };

    this.setState({
      message: combinedData,
      copying: isCopy,
      copiedMessage: isCopy,
    });
  };

  // show loader based on type and message state
  showLoader = () => {
    const {
      messageId,
      isCopy,
      isNew,
    } = this.props;

    const {
      message,
    } = this.state;

    if (isNew || isCopy) {
      return false;
    }

    if (messageId && message._id && !isCopy) {
      return false;
    }

    return true;
  };

  render() {
    const {
      type,
      isNew,
      isCopy,
      isDuplicate,
      isReRequest,
      showDisplayPlayer,
    } = this.props;

    const {
      message,
      activeSection,
      copying,
    } = this.state;

    return (
      <div className="MessageSidebar">
        {activeSection === 'details' ? (
          <MessageDetails
            type={type}
            message={message}
            isNew={isNew}
            isCopy={isCopy}
            copying={copying}
            isDuplicate={isDuplicate}
            isReRequest={isReRequest}
            showDisplayPlayer={showDisplayPlayer}
            onClose={this.onClose}
            onToggleChat={this.onToggleChat}
            onToggleHistory={this.onToggleHistory}
            onUpdate={this.onUpdate}
            onRefresh={this.getMessage}
          />
        ) : null}

        {activeSection === 'chat' ? (
          <MessageChat
            type={type}
            message={message}
            onClose={this.onClose}
            onToggleDetails={this.onToggleDetails}
            onToggleHistory={this.onToggleHistory}
            onRefresh={this.getMessage}
          />
        ) : null}

        {activeSection === 'history' ? (
          <MessageHistory
            message={message}
            onClose={this.onClose}
            onToggleDetails={this.onToggleDetails}
            onToggleChat={this.onToggleChat}
          />
        ) : null}

        {this.showLoader() && (
          <LoadingContent />
        )}
      </div>
    );
  }
}

MessageSidebar.propTypes = {
  type: PropTypes.oneOf([
    'request',
    'library',
  ]).isRequired,
  messageId: PropTypes.string,
  /** New Message */
  isNew: PropTypes.bool,
  isCopy: PropTypes.bool,
  /** Duplicate Message */
  isDuplicate: PropTypes.bool,
};

MessageSidebar.defaultProps = {
  messageId: null,
  isNew: false,
  isCopy: false,
  isDuplicate: false,
};

const mapDispatchToProps = {
  setPanel: GlobalActions.setPanel,
};

export default connect(null, mapDispatchToProps)(MessageSidebar);
